/**
 * Las lineas del diagrama.
 *
 * El dibujo es el clasico de un arbol genealogico, con conectores en angulo
 * recto, porque es el que dice sin palabras quien es que de quien:
 *
 *        [padre]═══●═══[madre]        barra de pareja, con el nudo en medio
 *                  │
 *          ┌───────┴───────┐          tronco hasta la barra de hermandad
 *          │       │       │
 *        [hijo]  [hijo]  [hijo]       los hermanos cuelgan de la misma barra
 *
 * Antes cada hijo colgaba del nudo con su propia curva suelta. Quedaba
 * bonito pero no se entendia: no habia forma de ver de un golpe que esos tres
 * eran hermanos entre si, ni donde acababa una familia y empezaba la de al
 * lado. La barra compartida lo resuelve de un vistazo.
 *
 * El tipo de union se dice con el trazo de la barra de pareja: continua si
 * estan casados, a trazos si es pareja sin matrimonio, y de puntos si no
 * consta. Asi se lee sin tener que abrir ninguna ficha.
 *
 * Todo se dibuja en DOS objetos de dibujo, uno para lo normal y otro para lo
 * resaltado, en vez de uno por linea. Con uno por linea habria una llamada de
 * dibujo por enlace y seria lo primero en romper los 60 fps.
 */
import { Graphics } from "pixi.js";
import type { Indice } from "../core/grafo.js";
import { ALTO_NODO, ALTURA_BARRA_HERMANOS, ANCHO_NODO } from "../layout/medidas.js";
import type { Nudo } from "../layout/layout.js";
import {
  ENLACE,
  ENLACE_ALFA,
  ENLACE_GROSOR,
  ENLACE_RESALTADO,
  ENLACE_RESALTADO_ALFA,
  VINCULO,
  VINCULO_ALFA,
  VINCULO_GROSOR,
} from "./tema.js";

export type TipoEnlace = "pareja" | "filiacion" | "hermandad";

export interface Segmento {
  tipo: TipoEnlace;
  familia: string;
  /** Las personas que toca este segmento, para el resaltado. */
  personas: string[];
  /** Pareja: los dos progenitores (el segundo puede faltar). */
  a?: string;
  b?: string;
  /** Filiacion y hermandad: todos los hijos, para dibujar una sola barra. */
  hijos?: string[];
  nudo?: Nudo;
  /** Tipo de union, que decide el trazo de la barra de pareja. */
  rel?: string;
}

/** Posicion de una persona, consultada en el momento de dibujar. */
export type Posicion = (handle: string) => { x: number; y: number } | undefined;

export function construirSegmentos(ix: Indice, nudos: Nudo[]): Segmento[] {
  const out: Segmento[] = [];
  const porFamilia = new Map<string, Nudo>();
  for (const n of nudos) porFamilia.set(n.familia, n);

  for (const f of ix.arbol.familias.values()) {
    const nudo = porFamilia.get(f.handle);
    const hijos = f.hijos.map((r) => r.hlink).filter((h) => ix.arbol.personas.has(h));

    if (nudo) {
      out.push({
        tipo: "pareja",
        familia: f.handle,
        personas: [...nudo.progenitores],
        a: nudo.progenitores[0],
        b: nudo.progenitores[1],
        nudo,
        rel: f.rel ?? "Unknown",
      });
      if (hijos.length) {
        // UN solo segmento por familia, no uno por hijo: asi la barra de
        // hermandad se dibuja una vez y no se van sumando opacidades donde
        // los trazos se pisan.
        out.push({
          tipo: "filiacion",
          familia: f.handle,
          personas: [...nudo.progenitores, ...hijos],
          hijos,
          nudo,
        });
      }
      continue;
    }

    // Familia sin progenitores: solo declara hermandad.
    if (hijos.length > 1) {
      out.push({ tipo: "hermandad", familia: f.handle, personas: hijos, hijos });
    }
  }

  return out;
}

export class Enlaces {
  readonly normales = new Graphics();
  readonly resaltados = new Graphics();

  private resaltado = new Set<string>();
  private ultimaEscala = -1;
  private sucio = true;

  constructor(
    private readonly segmentos: Segmento[],
    private readonly pos: Posicion,
  ) {
    this.normales.eventMode = "none";
    this.resaltados.eventMode = "none";
  }

  /** Marca que hay que redibujar porque las posiciones han cambiado. */
  invalidar(): void {
    this.sucio = true;
  }

  ponerResaltado(handles: Set<string>): void {
    this.resaltado = handles;
    this.sucio = true;
  }

  /**
   * Redibuja si hace falta. Devuelve si hubo trabajo, para poder medirlo.
   *
   * `atenuacion` baja la opacidad de lo no resaltado, y es lo que produce el
   * efecto de "se resaltan sus conexiones y el resto se atenua".
   */
  redibujar(escala: number, atenuacion: number): boolean {
    // El grosor se compensa con la escala para que las lineas se vean igual
    // de finas de cerca y de lejos, asi que un cambio apreciable de zoom
    // obliga a redibujar. Uno imperceptible, no.
    const cambioEscala = Math.abs(Math.log((escala + 1e-9) / (this.ultimaEscala + 1e-9)));
    if (!this.sucio && cambioEscala < 0.02) return false;
    this.ultimaEscala = escala;
    this.sucio = false;

    this.normales.clear();
    this.resaltados.clear();

    const hayResaltado = this.resaltado.size > 0;
    const compensar = (g: number): number => g / Math.max(escala, 0.08);

    for (const s of this.segmentos) {
      const destacado = hayResaltado && s.personas.some((p) => this.resaltado.has(p));
      const g = destacado ? this.resaltados : this.normales;
      const esVinculo = s.tipo !== "filiacion";

      let alfa: number;
      if (destacado) alfa = ENLACE_RESALTADO_ALFA;
      else if (hayResaltado) alfa = (esVinculo ? VINCULO_ALFA : ENLACE_ALFA) * atenuacion;
      else alfa = esVinculo ? VINCULO_ALFA : ENLACE_ALFA;
      if (alfa < 0.004) continue;

      const grosor = compensar(esVinculo ? VINCULO_GROSOR : ENLACE_GROSOR);
      const color = destacado ? ENLACE_RESALTADO : esVinculo ? VINCULO : ENLACE;

      if (!this.trazar(g, s, grosor)) continue;
      g.stroke({ width: grosor, color, alpha: alfa, cap: "round", join: "round" });

      // El nudo de la pareja se marca con un punto: es el centro del que
      // cuelga todo y ayuda a leer la pareja como una unidad.
      if (s.tipo === "pareja" && s.a && s.b && s.nudo) {
        g.circle(s.nudo.x, s.nudo.y, grosor * 1.9);
        g.fill({ color, alpha: alfa });
      }
    }

    return true;
  }

  /** Dibuja la geometria de un segmento. Devuelve si se pudo. */
  private trazar(g: Graphics, s: Segmento, grosor: number): boolean {
    switch (s.tipo) {
      case "pareja": {
        const nudo = s.nudo;
        if (!nudo) return false;
        const a = s.a ? this.pos(s.a) : undefined;
        if (!a) return false;
        const b = s.b ? this.pos(s.b) : undefined;

        if (!b) {
          // Un solo progenitor conocido: no hay barra que dibujar, el tronco
          // sale directamente de su tarjeta.
          return false;
        }

        // Barra horizontal entre los bordes que se miran de las dos tarjetas.
        const izq = Math.min(a.x, b.x) + ANCHO_NODO / 2;
        const der = Math.max(a.x, b.x) - ANCHO_NODO / 2;
        if (der <= izq) return false;

        const trazo = s.rel === "Married" ? "continuo" : s.rel === "Unmarried" ? "trazos" : "puntos";
        linea(g, izq, nudo.y, der, nudo.y, trazo, grosor);
        return true;
      }

      case "filiacion": {
        const nudo = s.nudo;
        const hijos = (s.hijos ?? [])
          .map((h) => this.pos(h))
          .filter((p): p is { x: number; y: number } => !!p);
        if (!nudo || !hijos.length) return false;

        const yHijos = hijos[0]?.y ?? 0;
        const barraY = yHijos - ALTO_NODO / 2 - ALTURA_BARRA_HERMANOS;

        // De donde sale el tronco: del nudo si hay pareja, o del borde de
        // abajo de la tarjeta si solo hay un progenitor.
        const unSoloProgenitor = nudo.progenitores.length === 1;
        const salida = unSoloProgenitor ? nudo.y + ALTO_NODO / 2 : nudo.y;
        if (barraY > salida) {
          g.moveTo(nudo.x, salida).lineTo(nudo.x, barraY);
        }

        // La barra de hermandad, de una sola pieza.
        const xs = hijos.map((h) => h.x);
        const izq = Math.min(nudo.x, ...xs);
        const der = Math.max(nudo.x, ...xs);
        if (der - izq > 0.5) g.moveTo(izq, barraY).lineTo(der, barraY);

        // Y una caida corta a cada hijo.
        for (const h of hijos) {
          g.moveTo(h.x, barraY).lineTo(h.x, h.y - ALTO_NODO / 2);
        }
        return true;
      }

      case "hermandad": {
        const hijos = (s.hijos ?? [])
          .map((h) => this.pos(h))
          .filter((p): p is { x: number; y: number } => !!p);
        if (hijos.length < 2) return false;

        // No hay progenitores de los que colgar, asi que la barra va por
        // encima y no nace de ningun sitio: eso mismo dice que la hermandad
        // consta pero los padres no.
        const y = (hijos[0]?.y ?? 0) - ALTO_NODO / 2 - ALTURA_BARRA_HERMANOS;
        const xs = hijos.map((h) => h.x);
        linea(g, Math.min(...xs), y, Math.max(...xs), y, "puntos", grosor);
        for (const h of hijos) {
          g.moveTo(h.x, y).lineTo(h.x, h.y - ALTO_NODO / 2);
        }
        return true;
      }
    }
  }

  destruir(): void {
    this.normales.destroy();
    this.resaltados.destroy();
  }
}

/**
 * Una linea continua, a trazos o de puntos.
 *
 * Pixi no dibuja lineas discontinuas, asi que se trocean a mano. El tamano
 * del trozo va con el grosor, que ya esta compensado con el zoom, para que el
 * patron se vea igual de cerca y de lejos.
 */
function linea(
  g: Graphics,
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  trazo: "continuo" | "trazos" | "puntos",
  grosor: number,
): void {
  if (trazo === "continuo") {
    g.moveTo(x1, y1).lineTo(x2, y2);
    return;
  }
  const largo = Math.hypot(x2 - x1, y2 - y1);
  if (largo < 0.5) return;
  const trozo = trazo === "trazos" ? grosor * 5 : grosor * 1.2;
  const hueco = trazo === "trazos" ? grosor * 4 : grosor * 3;
  const paso = trozo + hueco;
  const ux = (x2 - x1) / largo;
  const uy = (y2 - y1) / largo;

  // Se centra el patron para que los dos extremos queden simetricos.
  const n = Math.max(1, Math.round((largo + hueco) / paso));
  const sobra = largo - (n * paso - hueco);
  let d = sobra / 2;
  for (let i = 0; i < n; i++) {
    const f = Math.min(d + trozo, largo);
    g.moveTo(x1 + ux * d, y1 + uy * d).lineTo(x1 + ux * f, y1 + uy * f);
    d += paso;
  }
}
